import type { ImageProps } from 'fitify-ui-onboarding/src/types/ui'
import { useGrowthBook } from 'fitify-ui-onboarding/src/utils/growthbook/generated'
import { useTranslation } from 'next-i18next/pages'
import { useCallback, useMemo } from 'react'

import { useLayoutTheme } from 'components/GenderFunnel/Layout/useLayoutTheme'
import { DEFAULT_IMAGE_VARIANT } from 'data/images'
import type {
  CategoryPageImages,
  CategoryPages,
  CategoryVariants,
  ImageCategories,
} from 'types/images'
import { Question } from 'types/state'
import { getImageProps } from 'utils/images'
import { useDataState } from 'utils/state'

export const useCategoryImageVariant = <TCategory extends ImageCategories>(
  category?: TCategory
): string => {
  const { data } = useDataState()
  const growthBook = useGrowthBook()

  const isOlderAgeBucket = useCallback(
    (ageBucket?: Question.AgeBucket) => {
      switch (ageBucket) {
        case Question.AgeBucket.Age_40_49:
        case Question.AgeBucket.Age_50_plus:
        case Question.AgeBucket.Age_50_59:
        case Question.AgeBucket.Age_60_plus:
        case Question.AgeBucket.Age_60_69:
        case Question.AgeBucket.Age_70_plus:
          return (
            growthBook.evalFeature('age_based_assets_enabled')?.value === true
          )
        default:
          return false
      }
    },
    [growthBook]
  )

  const getAgeBasedVariant = useCallback(
    (ageBucket?: Question.AgeBucket) =>
      isOlderAgeBucket(ageBucket) ? 'memojiOlder' : DEFAULT_IMAGE_VARIANT,
    [isOlderAgeBucket]
  )

  const getFemaleCharacterVariant = useCallback(
    (ageBucket?: Question.AgeBucket) => {
      const characterStyle = !data.common.onboardingVariant
        ? growthBook.evalFeature('f01_character_style')?.value
        : undefined
      const isOlder = isOlderAgeBucket(ageBucket)
      switch (characterStyle) {
        case 'memoji_purple':
          return isOlder ? 'memojiPurpleOlder' : 'memojiPurple'
        case 'memoji_brown':
          return isOlder ? 'memojiBrownOlder' : 'memojiBrown'
        default:
          return getAgeBasedVariant(ageBucket)
      }
    },
    [
      data.common.onboardingVariant,
      getAgeBasedVariant,
      growthBook,
      isOlderAgeBucket,
    ]
  )

  switch (category) {
    case 'm01': {
      if (!data.common.onboardingVariant) {
        const characterStyle = growthBook.evalFeature(
          'm01_character_style'
        )?.value
        if (characterStyle === 'realistic') {
          return 'realistic'
        }
      }
      return getAgeBasedVariant(data[category].age_bucket)
    }
    case 'milicam01':
      return growthBook.evalFeature('milicam01_character_style')?.value ===
        'black_male'
        ? 'blackMale'
        : DEFAULT_IMAGE_VARIANT
    case 'f01':
    case 'hopif01':
    case 'wallpif01':
      return getFemaleCharacterVariant(data[category].age_bucket)
    default:
      return DEFAULT_IMAGE_VARIANT
  }
}

export const useFunnelImages = <TCategory extends ImageCategories>(
  funnel: TCategory
) => {
  const variant = useCategoryImageVariant(funnel)
  const { i18n } = useTranslation()
  const theme = useLayoutTheme()

  const getFunnelImageProps = useCallback(
    <
      TVariant extends CategoryVariants<TCategory>,
      TPages extends CategoryPages<TCategory, TVariant>,
      TImage extends CategoryPageImages<TCategory, TVariant, TPages>,
    >(
      page: TPages,
      imageName: TImage
    ): ImageProps =>
      getImageProps(funnel, page, imageName, {
        variant: variant as TVariant,
        locale: i18n.language,
        theme: theme.type,
      }),
    [funnel, i18n.language, theme.type, variant]
  )

  return { getFunnelImageProps }
}

export const useImageProps = <
  TCategory extends ImageCategories,
  TVariant extends CategoryVariants<TCategory>,
  TPages extends CategoryPages<TCategory, TVariant>,
  TImage extends CategoryPageImages<TCategory, TVariant, TPages>,
>(
  category: TCategory,
  page: TPages,
  imageName: TImage
): ImageProps => {
  const { getFunnelImageProps } = useFunnelImages<TCategory>(category)

  return useMemo(
    () => getFunnelImageProps<TVariant, TPages, TImage>(page, imageName),
    [getFunnelImageProps, page, imageName]
  )
}
